"use client";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

/**
 * Route-level guard. Mirrors the backend authorization (checkPermission / isAdmin)
 * exactly so it can never lock out a user the API would allow:
 *   - "Super Admin" role always passes
 *   - otherwise the role's permissions must include `required`
 *
 * While auth is still resolving (user not yet hydrated) we render children and let
 * RootProvider handle the unauthenticated redirect — this avoids false lock-outs/flicker.
 */
export default function ProtectedRoute({ required, children }) {
    const { user, isLoading } = useSelector((state) => state.auth);

    // Auth not resolved yet — don't block.
    if (isLoading || !user) {
        return children;
    }

    if (user?.role?.name === "Super Admin") {
        return children;
    }

    let permissions = [];
    try {
        const raw = user?.role?.permissions;
        if (typeof raw === "string") permissions = JSON.parse(raw);
        else if (Array.isArray(raw)) permissions = raw;
    } catch (e) {
        permissions = [];
    }

    if (required && permissions.includes(required)) {
        return children;
    }

    // Authenticated but not permitted — send to the dashboard home.
    return <Navigate to="/dashboard" replace />;
}
